import joi from 'joi';
import {Map, Record as record} from 'immutable';

import FeatureFlagStore from '../../stores/FeatureFlagStore';
import { construct, fields as resolvers } from '../../helpers/LinkMapper';
import { getResolvers, getDefaults } from '../../helpers/Records';

export const PreferenceType = {
    SMS: 'SMS',
    EMAIL: 'EMAIL'
};

let emailMarketingFields = [ 'emailServiceUpdates', 'emailPromotions', 'emailProducts'];
const smsMarketingFields = [ 'smsServiceUpdates', 'smsPromotions', 'smsProducts' ];

const marketingFieldVisibleFlags = {
    emailServiceUpdates: 'enableServicePreference',
    emailPromotions: 'enablePromotionsPreference',
    emailProducts: 'enableProductsPreference',
    smsServiceUpdates: 'enableServicePreference',
    smsPromotions: 'enablePromotionsPreference',
    smsProducts: 'enableProductsPreference',
    autoDelayRepay: 'enableAutoDelayRepay'
};

const marketingFieldDisabledForUnder16s = {
    emailServiceUpdates: false,
    emailPromotions: true,
    emailProducts: true,
    smsServiceUpdates: false,
    smsPromotions: true,
    smsProducts: true,
    autoDelayRepay: false,
    autoDelayRepayApp: false,
    autoDelayRepayEmail: false,
};

const schema = {
    emailServiceUpdates: [resolvers.boolean, false],
    smsServiceUpdates: [resolvers.boolean, false],
    emailPromotions: [resolvers.boolean, false],
    smsPromotions: [resolvers.boolean, false],
    emailProducts: [resolvers.boolean, false],
    smsProducts: [resolvers.boolean, false],
    autoDelayRepay: [resolvers.boolean, false],
    autoDelayRepayApp: [resolvers.boolean, false],
    autoDelayRepayEmail: [resolvers.boolean, false],
    timestamp: [resolvers.datetime]
};

export default class UserPreferences extends record(getDefaults(schema)) {
    static fromJSON(json = {}) {
        return construct(UserPreferences, json, getResolvers(schema));
    }

    getSchema () {
        return {
            emailServiceUpdates: joi.boolean(),
            smsServiceUpdates: joi.boolean(),
            emailPromotions: joi.boolean(),
            smsPromotions: joi.boolean(),
            emailProducts: joi.boolean(),
            smsProducts: joi.boolean(),
            autoDelayRepay: joi.boolean(),
            autoDelayRepayApp: joi.boolean(),
            autoDelayRepayEmail: joi.boolean(),
        };
    }

    toggleAllMarketingPreferences (type, value, isOver16) {
        let relevantEmailFields = emailMarketingFields;

        let preferences = this;

        const updateFieldsConditionally = (fields) => {
            fields.forEach(field => {
                const shown = FeatureFlagStore.get(marketingFieldVisibleFlags[field]);
                const disabled = !isOver16 && marketingFieldDisabledForUnder16s[field];
                if (shown && !disabled) {
                    preferences = preferences.set(field, value);
                }
            });
        };

        if (type === PreferenceType.EMAIL) {
            updateFieldsConditionally(relevantEmailFields);
        }
        if (type === PreferenceType.SMS) {
            updateFieldsConditionally(smsMarketingFields);
        }

        return preferences;
    }

    resetAllDisabledMarketingFields () {
        let preferences = this;

        const resetFieldsConditionally = fields => {
            fields.forEach(field => {
                if (marketingFieldDisabledForUnder16s[field]) {
                    preferences = preferences.set(field, false);
                }
            });
        };

        const allMarketingFields = emailMarketingFields.concat(smsMarketingFields);
        resetFieldsConditionally(allMarketingFields);
        return preferences;
    }

    checkAllEnabledFieldsSetToTrue (fields, isOver16) {
        return fields.every(field => {
            const shown = FeatureFlagStore.get(marketingFieldVisibleFlags[field]);
            const disabled = !isOver16 && marketingFieldDisabledForUnder16s[field];
            return this.get(field) || disabled || !shown;
        });
    }

    isEveryEnabledEmailPrefSetToTrue (isOver16) {
        let relevantFields = emailMarketingFields;
        return this.checkAllEnabledFieldsSetToTrue(relevantFields, isOver16);
    }

    isEveryEnabledSmsPrefSetToTrue (isOver16) {
        return this.checkAllEnabledFieldsSetToTrue(smsMarketingFields, isOver16);
    }

    isSomeSmsPrefSetToTrue () {
        return smsMarketingFields.some(field => this.get(field));
    }

    hasEmailProductsSetToTrue () {
        return this.emailProducts;
    }

    hasEmailPromotionsSetToTrue () {
        return this.emailPromotions;
    }

    hasDifferentPreferencesTo (preference) {
        const allMarketingFields = emailMarketingFields.concat(smsMarketingFields);
        return allMarketingFields.some(field => this.get(field) !== preference.get(field));
    }

    isEveryUnsubscribeFieldSetToFalse () {
        return emailMarketingFields
            .every(field => !this.get(field));
    }

    hasMultipleEmailFieldsSetToTrue () {
        const fields = emailMarketingFields;
        return fields.filter(field => this.get(field)).length > 1;
    }

    hasNoEmailFieldsSetToTrue () {
        const fields = emailMarketingFields;
        return fields.every(field => !this.get(field));
    }

    toggleAllEmailFields (originalPreferences, value) {
        const fields = emailMarketingFields
            .filter(field => originalPreferences.get(field));
        let preferences = this;
        fields.forEach(field => {
            preferences = preferences.set(field, value);
        });
        return preferences;
    }

    static getRegistrationPreferencesFields(form) {
        return new Map({
            emailServiceUpdates: form.get('emailServiceUpdates'),
            emailPromotions: form.get('emailPromotions'),
            emailProducts: form.get('emailProducts'),
        });
    }
}
